import { useRef, useState } from 'react';
import { MicrophoneIcon, StopIcon, SpinnerGapIcon } from '@phosphor-icons/react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * GL-SPEC-APP-038 — VoiceDictateButton
 *
 * Small mic button suitable for placing next to any textarea. Tap to
 * start recording, tap again to stop. On stop, the audio is uploaded
 * to `/ai/transcribe` (Whisper via Emergent LLM key) and the returned
 * text is appended to the current field value via `onTranscript`.
 *
 * Uses the browser MediaRecorder API. iPhone Safari 14+ supports this
 * with audio/mp4 by default — we let the browser pick the mime type.
 *
 * Props:
 *   onTranscript(text) — called with the transcript on success
 *   disabled           — hide/disable the button
 *   testid             — data-testid override
 */
export default function VoiceDictateButton({ onTranscript, disabled = false, testid = 'voice-dictate-btn', variant = 'inline' }) {
  const [state, setState] = useState('idle'); // idle | recording | transcribing
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  async function startRecording() {
    if (state !== 'idle') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Let the browser pick a supported mime (Safari: audio/mp4, Chrome: audio/webm)
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
          setState('transcribing');
          const form = new FormData();
          // pick a filename extension matching the mime for backend format detection
          const ext = (mr.mimeType || '').includes('mp4') ? 'mp4' : (mr.mimeType || '').includes('wav') ? 'wav' : 'webm';
          form.append('file', blob, `voice.${ext}`);
          form.append('language', 'en');
          const { data } = await api.post('/ai/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const text = (data?.text || '').trim();
          if (text) {
            onTranscript?.(text);
            toast.success('Transcript added');
          } else {
            toast.warning('No speech detected');
          }
        } catch (e) {
          toast.error(formatApiError(e));
        } finally {
          streamRef.current?.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
          chunksRef.current = [];
          setState('idle');
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setState('recording');
    } catch (e) {
      const msg = e?.name === 'NotAllowedError'
        ? 'Microphone permission denied. Enable it in your browser settings.'
        : e?.message || 'Could not access microphone';
      toast.error(msg);
      setState('idle');
    }
  }

  function stopRecording() {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    mediaRecorderRef.current = null;
  }

  function onClick() {
    if (disabled) return;
    if (state === 'idle') startRecording();
    else if (state === 'recording') stopRecording();
    // 'transcribing' — do nothing until it finishes
  }

  const busy = state === 'transcribing';
  const recording = state === 'recording';

  // GL-DESIGN-V3 — 80px round "Dictate" hero button used in the finding
  // capture sheet. Idle: raised surface + navy mic. Recording: P1 red,
  // inset shadow, stop icon, "Listening…" label underneath.
  if (variant === 'hero') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled || busy}
          data-testid={testid}
          aria-label={recording ? 'Stop recording' : 'Dictate'}
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            border: 0,
            cursor: disabled || busy ? 'not-allowed' : 'pointer',
            background: recording ? 'var(--v3-p1)' : 'var(--v3-surface)',
            color: recording ? '#fff' : 'var(--v3-navy)',
            boxShadow: recording ? 'inset 4px 4px 9px rgba(0,0,0,.25)' : 'var(--v3-shadow-raised)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: disabled ? 0.5 : 1,
            transition: 'background-color 160ms ease, box-shadow 160ms ease',
          }}
        >
          {recording ? (
            <StopIcon size={30} weight="fill" />
          ) : busy ? (
            <SpinnerGapIcon size={30} weight="bold" className="animate-spin" />
          ) : (
            <MicrophoneIcon size={30} weight="fill" />
          )}
        </button>
        <span
          data-testid={`${testid}-label`}
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10.5,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: recording ? 'var(--v3-p1)' : 'var(--v3-muted)',
          }}
        >
          {recording ? 'Listening…' : busy ? 'Transcribing…' : 'Dictate'}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      data-testid={testid}
      title={
        recording ? 'Stop recording' :
        busy ? 'Transcribing…' :
        'Dictate — tap to record, tap again to stop'
      }
      className="inline-flex items-center gap-1.5 h-8 px-2.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border"
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.06em',
        color: recording ? '#991b1b' : busy ? '#374151' : 'var(--gl-navy, #142654)',
        borderColor: recording ? '#fca5a5' : busy ? '#d1d5db' : 'var(--gl-navy, #142654)',
        background: recording ? '#fef2f2' : busy ? '#f9fafb' : '#ffffff',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled || busy ? 'not-allowed' : 'pointer',
      }}
    >
      {recording ? (
        <>
          <StopIcon size={13} weight="fill" />
          <span>Stop</span>
        </>
      ) : busy ? (
        <>
          <SpinnerGapIcon size={13} weight="bold" className="animate-spin" />
          <span>Transcribing…</span>
        </>
      ) : (
        <>
          <MicrophoneIcon size={13} weight="bold" />
          <span>Dictate</span>
        </>
      )}
    </button>
  );
}
